import type { FormSchemaV1 } from "@rwaforms/shared";

const STORAGE_KEY = "rwaforms.offline.autosave";
const _MAX_RETRY_ATTEMPTS = 5;
const INITIAL_RETRY_DELAY_MS = 1000;
const MAX_RETRY_DELAY_MS = 30000;

// Queue mutex to prevent concurrent operations
let queueLock = false;
const lockWaiters: Array<() => void> = [];

export type AutosaveTask =
  | {
    id: string;
    type: "create";
    formId: string;
    schema: FormSchemaV1;
    createdAt: number;
    retryAttempt?: number;
    retryDelay?: number;
  }
  | {
    id: string;
    type: "update";
    versionId: string;
    schema: FormSchemaV1;
    createdAt: number;
    retryAttempt?: number;
    retryDelay?: number;
  };

function readQueue(): AutosaveTask[] {
  if (typeof window === "undefined") {
    return [];
  }
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw) as AutosaveTask[];
    if (!Array.isArray(parsed)) return [];
    return parsed;
  } catch {
    return [];
  }
}

function writeQueue(tasks: AutosaveTask[]): void {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(tasks));
  } catch {
    // Ignore quota errors.
  }
}

function generateId(): string {
  if (typeof crypto !== "undefined" && "randomUUID" in crypto) {
    return crypto.randomUUID();
  }
  return `autosave-${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

function cloneSchema(schema: FormSchemaV1): FormSchemaV1 {
  if (typeof structuredClone === "function") {
    return structuredClone(schema);
  }
  return JSON.parse(JSON.stringify(schema)) as FormSchemaV1;
}

/**
 * Acquire queue lock (mutex)
 */
async function acquireLock(): Promise<void> {
  return new Promise((resolve) => {
    if (!queueLock) {
      queueLock = true;
      resolve();
      return;
    }
    lockWaiters.push(resolve);
  });
}

/**
 * Release queue lock
 */
function releaseLock(): void {
  queueLock = false;
  const next = lockWaiters.shift();
  if (next) {
    queueLock = true;
    next();
  }
}

/**
 * Calculate exponential backoff delay
 */
function calculateBackoffDelay(attempt: number): number {
  const delay = Math.min(
    INITIAL_RETRY_DELAY_MS * Math.pow(2, attempt),
    MAX_RETRY_DELAY_MS
  );
  // Add jitter to prevent thundering herd
  return delay + Math.random() * 1000;
}

export async function enqueueAutosaveTask(task: Omit<AutosaveTask, "id" | "createdAt">): Promise<void> {
  await acquireLock();
  try {
    const queue = readQueue();

    // Conflict resolution: remove conflicting tasks
    const filtered = queue.filter((existing) => {
      if (task.type === "create" && existing.type === "create") {
        return false; // supersede existing create
      }
      if (task.type === "update" && existing.type === "update") {
        // Keep only if different versionId, otherwise supersede
        // Cast through unknown since Omit loses discriminated union info
        return existing.versionId !== (task as unknown as { versionId: string }).versionId;
      }
      return true;
    });

    let newTask: AutosaveTask;
    if (task.type === "create") {
      newTask = {
        id: generateId(),
        type: "create" as const,
        formId: (task as unknown as { formId: string }).formId,
        schema: cloneSchema(task.schema),
        createdAt: Date.now(),
      };
    } else {
      newTask = {
        id: generateId(),
        type: "update" as const,
        versionId: (task as unknown as { versionId: string }).versionId,
        schema: cloneSchema(task.schema),
        createdAt: Date.now(),
      };
    }

    filtered.push(newTask);

    writeQueue(filtered);
  } finally {
    releaseLock();
  }
}

export async function drainAutosaveTasks(): Promise<AutosaveTask[]> {
  await acquireLock();
  try {
    const queue = readQueue().sort((a, b) => a.createdAt - b.createdAt);
    writeQueue([]);
    return queue;
  } finally {
    releaseLock();
  }
}

/**
 * Synchronous version for backward compatibility (use with caution)
 */
export function drainAutosaveTasksSync(): AutosaveTask[] {
  if (queueLock) {
    console.warn("[offlineQueue] Attempted to drain queue while locked, returning empty array");
    return [];
  }
  const queue = readQueue().sort((a, b) => a.createdAt - b.createdAt);
  writeQueue([]);
  return queue;
}

export async function requeueAutosaveTasks(tasks: AutosaveTask[], retryAttempt?: number): Promise<void> {
  await acquireLock();
  try {
    if (!tasks.length) {
      writeQueue([]);
      return;
    }

    const queue = readQueue();

    // Add retry metadata to tasks
    const tasksWithRetry = tasks.map((task) => ({
      ...task,
      retryAttempt: (retryAttempt ?? 0) + 1,
      retryDelay: calculateBackoffDelay(retryAttempt ?? 0),
    }));

    // Prepend failed tasks to queue (they get priority)
    writeQueue([...tasksWithRetry, ...queue]);
  } finally {
    releaseLock();
  }
}

/**
 * Synchronous version for backward compatibility (use with caution)
 */
export function requeueAutosaveTasksSync(tasks: AutosaveTask[]): void {
  if (queueLock) {
    console.warn("[offlineQueue] Attempted to requeue while locked");
    return;
  }
  if (!tasks.length) {
    writeQueue([]);
    return;
  }
  const queue = readQueue();
  writeQueue([...tasks, ...queue]);
}

export async function hasQueuedAutosaveTasks(): Promise<boolean> {
  await acquireLock();
  try {
    return readQueue().length > 0;
  } finally {
    releaseLock();
  }
}

/**
 * Synchronous version for backward compatibility
 */
export function hasQueuedAutosaveTasksSync(): boolean {
  return readQueue().length > 0;
}
